import { Component, computed, inject, Input, OnInit, signal } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { BaseModalComponent, ToleranceMeterSegment } from '@card-software/library';
import { AuthService } from '@card-software/nexus-utilities';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { BalanceoAvailableRoll, BalanceoColorGroup, BalanceoFabricRow, BalanceoSummary } from '@app/models/production/balanceo';
import { FinishingRoles } from '@app/models/roles/roles-group';
import { ProductionCardsProxyService } from '@app/proxies/production/production-card.proxy.service';
import { CuadrajeBalanceoAdapterService } from '@app/services/cuadraje-balanceo-adapter.service';
import { LocaleHelperService } from '@app/services/locale-helper.service';

export type BalanceoRatioMode = 'ratio' | 'proportion';

/** Same color for every fabric's bar — the anchor doesn't need to look different from the rest. */
const FABRIC_COLOR = 'var(--p-green-500)';

/** A fabric that came back over its recalculated target, so Split can trim it. */
export interface BalanceoSplitCandidate {
  fabricType: string;
  excess: number;
}

@Component({
  selector: 'app-balanceo-modal',
  templateUrl: './balanceo-modal.component.html',
  styleUrl: './balanceo-modal.component.scss',
})
export class BalanceoModalComponent extends BaseModalComponent<null> implements OnInit {
  private readonly productionCardProxy = inject(ProductionCardsProxyService);
  private readonly adapter = inject(CuadrajeBalanceoAdapterService);
  private readonly localeHelperService = inject(LocaleHelperService);

  /** Resolved by the caller before the modal opens — see roll-final-qc.component.ts. */
  @Input() productionCardId!: string;

  /** Only visible with this role — otherwise every user sees Ratio, the new default for everyone. */
  readonly canViewProportion = AuthService.hasRole(FinishingRoles.ProductionCardBalancingProportionViewer);

  ratioMode = signal<BalanceoRatioMode>('ratio');
  ratioModeOptions: { label: string; value: BalanceoRatioMode }[] = [
    { label: 'Ratio (÷ ancla)', value: 'ratio' },
    { label: 'Proporción (÷ total)', value: 'proportion' },
  ];

  private readonly liveSummary = signal<BalanceoSummary | null>(null);
  isLoading = signal(0);

  get summary(): BalanceoSummary | null {
    return this.liveSummary();
  }

  colors = computed<BalanceoColorGroup[]>(() => this.liveSummary()?.colors ?? []);

  private incrementLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private decrementLoading(): void {
    this.isLoading.update((v) => Math.max(0, v - 1));
  }

  ngOnInit(): void {
    this.load();
  }

  onClose(): void {
    this.close({ action: 'close', data: null });
  }

  /** What the Production Card was planned for, across every fabric. */
  planTotal(color: BalanceoColorGroup): number {
    return color.fabrics.reduce((total, fabric) => total + fabric.plan, 0);
  }

  /** Total of what QC really approved, across every fabric — the base the ratio line reads against. */
  realTotal(color: BalanceoColorGroup): number {
    return color.fabrics.reduce((total, fabric) => total + fabric.real, 0);
  }

  /**
   * The fabric with the largest target quantity of the Work Order — the "anchor" of Ratio mode.
   * Distinct from `row.isAnchor` (the Body fabric, which keeps governing the pounds-based
   * recalculated target regardless of this toggle) — the two can disagree in a rare WO shape.
   */
  ratioAnchorRow(color: BalanceoColorGroup): BalanceoFabricRow | undefined {
    return color.fabrics.reduce<BalanceoFabricRow | undefined>(
      (max, fabric) => (!max || fabric.targetRatio > max.targetRatio ? fabric : max),
      undefined,
    );
  }

  /**
   * Share of the real total each fabric holds today. In Proportion mode that's the real total
   * across every fabric; in Ratio mode it's the ratio anchor's own real quantity instead.
   */
  realRatio(color: BalanceoColorGroup, row: BalanceoFabricRow): number {
    const total = this.ratioMode() === 'proportion' ? this.realTotal(color) : (this.ratioAnchorRow(color)?.real ?? 0);

    return total > 0 ? (row.real / total) * 100 : 0;
  }

  /**
   * Target ratio as shown, following the same Proportion/Ratio split as `realRatio`. `row.targetRatio`
   * itself never changes — it always comes from the API as the Work Order-wide "÷ total" share.
   */
  targetRatioDisplay(color: BalanceoColorGroup, row: BalanceoFabricRow): number {
    if (this.ratioMode() === 'proportion') return row.targetRatio;

    const anchorTargetRatio = this.ratioAnchorRow(color)?.targetRatio ?? 0;

    return anchorTargetRatio > 0 ? (row.targetRatio / anchorTargetRatio) * 100 : 0;
  }

  /** How much of its own plan the body fabric actually reached. Everything else follows it. */
  anchorRatio(color: BalanceoColorGroup): number {
    const anchor = color.fabrics.find((fabric) => fabric.isAnchor);
    if (!anchor || anchor.plan <= 0) return 1;

    return anchor.real / anchor.plan;
  }

  /**
   * The anchor keeps its own plan as the target — it is what defines the balance. Every other
   * fabric is scaled down (or up) to the proportion the anchor really hit.
   */
  target(color: BalanceoColorGroup, row: BalanceoFabricRow): number {
    return row.isAnchor ? row.plan : row.plan * this.anchorRatio(color);
  }

  /** Positive is over the ratio, negative is short of it. */
  difference(color: BalanceoColorGroup, row: BalanceoFabricRow): number {
    return row.real - this.target(color, row);
  }

  /**
   * What Rolling would have to do with this fabric to land on the ratio. The anchor goes through
   * the exact same formula as every other fabric — its `target` is just its own plan, so this
   * reads as "how far off its own plan the anchor came back", same shape as the rest.
   */
  actionLabel(color: BalanceoColorGroup, row: BalanceoFabricRow): string {
    const difference = Math.round(this.difference(color, row));
    if (difference > 0) return `Quitar ~${difference} ${this.summary?.unit}`;
    if (difference < 0) return `Faltan ~${Math.abs(difference)} ${this.summary?.unit}`;

    return 'En balance';
  }

  /** Rounded the same way actionLabel rounds it, so a difference that reads "En balance" never lands on a different severity than success. */
  actionSeverity(color: BalanceoColorGroup, row: BalanceoFabricRow): 'secondary' | 'success' | 'warn' | 'danger' {
    const difference = Math.round(this.difference(color, row));
    if (difference > 0) return 'danger';
    if (difference < 0) return 'warn';

    return 'success';
  }

  /**
   * The bar itself reads as ratio actual vs ratio objetivo, not pounds — one segment filled to
   * this fabric's share of the real total. Same color for every fabric, anchor included — users
   * already know which one is the anchor from the ANCLA mark, the bar doesn't need to call it out.
   */
  segments(color: BalanceoColorGroup, row: BalanceoFabricRow): ToleranceMeterSegment[] {
    return [{ label: row.fabricType, value: this.realRatio(color, row), color: FABRIC_COLOR }];
  }

  /**
   * There is no client tolerance here — the target ratio is a single point, so both limits sit on
   * it and the dashed line marks exactly where the excess starts.
   */
  scaleMax(color: BalanceoColorGroup, row: BalanceoFabricRow): number {
    return Math.max(this.targetRatioDisplay(color, row), this.realRatio(color, row));
  }

  /**
   * Where the target ratio's dashed line sits, as a % from the bottom of the bar — the meter draws
   * the line itself but renders no label, so this positions the "93.47%"-style text next to it. Same
   * scaleMin(0)/scaleMax mapping the meter uses internally, so it lines up with the dashed mark.
   */
  targetLabelPosition(color: BalanceoColorGroup, row: BalanceoFabricRow): number {
    const max = this.scaleMax(color, row);

    return max > 0 ? Math.min(100, Math.max(0, (this.targetRatioDisplay(color, row) / max) * 100)) : 0;
  }

  /** Only the fabrics past the ratio can be trimmed, so the Split hint comes from the bars. */
  splitCandidates(color: BalanceoColorGroup): BalanceoSplitCandidate[] {
    return color.fabrics
      .filter((fabric) => !fabric.isAnchor && this.difference(color, fabric) > 0)
      .map((fabric) => ({ fabricType: fabric.fabricType, excess: Math.round(this.difference(color, fabric)) }));
  }

  /** The structured message shown on the Split hyperlink — same translateWithFallback + manual placeholder pattern already used in reassign-rolls.component.ts. */
  splitHintText(candidate: BalanceoSplitCandidate): string {
    const template = this.localeHelperService.translateWithFallback('Balanceo_SplitHint', 'Haga split de ~{0} {1} de {2}');
    return template
      .replace('{0}', String(candidate.excess))
      .replace('{1}', this.summary?.unit ?? '')
      .replace('{2}', candidate.fabricType);
  }

  /**
   * There is no single roll to target here — `real` is the sum of every roll QC approved for this
   * fabric, and none of them are singled out on this screen. Hands off to the existing Split Roll
   * flow, where the user picks which physical roll to split and enters the weight.
   *
   * Opens in a new tab instead of navigating — the modal stays open and on the same data, so the
   * user can come back and hit Reload instead of losing this Balanceo view to do the split.
   */
  onSplit(candidate: BalanceoSplitCandidate): void {
    void candidate;
    window.open('/roll-split', '_blank');
  }

  /**
   * Reassigning a leftover roll is a full compatibility-checked flow of its own — hand off to the
   * existing Reassign Roll page instead of reassigning directly from here, pre-loaded with this
   * roll's origin and the Production Card being balanced as the target. Opens in a new tab for the
   * same reason as onSplit.
   */
  onReassign(roll: BalanceoAvailableRoll): void {
    if (!roll.originProductionCardId) {
      GlobalMessageUtil.error('Error', 'No se pudo determinar la production card de origen de este rollo');
      return;
    }

    const params = new URLSearchParams({
      originPcId: roll.originProductionCardId,
      targetPcId: this.productionCardId,
    });
    window.open(`/reassign-rolls?${params.toString()}`, '_blank');
  }

  /** Reloads the whole balance so both the bars and the leftover table reflect the latest data — also what the header's Reload button re-runs. */
  load(): void {
    this.incrementLoading();
    this.productionCardProxy
      .getProductionCardBalance(this.productionCardId)
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe({
        next: (response) => {
          if (response.data) {
            this.liveSummary.set(this.adapter.toBalanceoSummary(response.data));
          }
        },
      });
  }
}
